/* Live HTML preview that mirrors the ReportLab invoice PDF (pdf_gen.invoice_pdf).
   Presentation only. Uses fictional demo data — never real customer information. */

export const TEMPLATE_PRESETS = {
  modern:   { label: "Modern Professional", desc: "Clean white layout with a contemporary business look.", accent: "#0F766E", dark: "#252A31", font: "sans",  header_style: "standard", density: "comfortable", labour_parts: "unified" },
  classic:  { label: "Classic Garage",      desc: "Traditional automotive workshop invoice.",             accent: "#B45309", dark: "#1F2937", font: "serif", header_style: "band",     density: "comfortable", labour_parts: "grouped" },
  premium:  { label: "Premium",             desc: "Elegant layout with a strong branded header.",         accent: "#0B1F3A", dark: "#0B1F3A", font: "sans",  header_style: "band",     density: "comfortable", labour_parts: "unified" },
  detailed: { label: "Detailed Workshop",   desc: "Clear separation of labour, parts and vehicle info.",  accent: "#1D4ED8", dark: "#252A31", font: "sans",  header_style: "standard", density: "comfortable", labour_parts: "grouped" },
  minimal:  { label: "Minimal",             desc: "Compact, simple and easy to read.",                    accent: "#334155", dark: "#0F172A", font: "sans",  header_style: "minimal",  density: "compact",     labour_parts: "unified" },
};

export const SECTION_DEFAULTS = {
  show_logo: true, show_vehicle: true, show_payment_details: true,
  show_terms: true, show_signature: true, show_thank_you: true, show_contact: true,
};

const FONT_FAMILY = {
  sans: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "'IBM Plex Mono', ui-monospace, monospace",
};

export function resolveDesign(raw = {}) {
  const tpl = TEMPLATE_PRESETS[raw.template] ? raw.template : "modern";
  const d = { template: tpl, ...TEMPLATE_PRESETS[tpl], ...SECTION_DEFAULTS };
  if (raw.primary_color) d.accent = raw.primary_color;
  if (raw.secondary_color) d.dark = raw.secondary_color;
  if (FONT_FAMILY[raw.font]) d.font = raw.font;
  if (["standard", "band", "minimal"].includes(raw.header_style)) d.header_style = raw.header_style;
  if (["comfortable", "compact"].includes(raw.density)) d.density = raw.density;
  if (["unified", "grouped"].includes(raw.labour_parts)) d.labour_parts = raw.labour_parts;
  Object.keys(SECTION_DEFAULTS).forEach((k) => { if (typeof raw[k] === "boolean") d[k] = raw[k]; });
  d.footer_text = raw.footer_text || "";
  const sz = Number(raw.logo_size);
  d.logo_size = Number.isFinite(sz) ? Math.min(24, Math.max(8, sz)) : 14;
  d.logo_crop = raw.logo_crop === true;
  const allowed = ["payment_details", "thank_you", "terms", "signature"];
  const order = (raw.section_order || []).filter((x) => allowed.includes(x));
  allowed.forEach((x) => { if (!order.includes(x)) order.push(x); });
  d.section_order = order;
  return d;
}

const DEMO = {
  garage: { name: "Demo Motors Ltd", tagline: "MOT · Servicing · Repairs", website: "www.demomotors.co.uk", phone: "01234 567890", email: "hello@demomotors.co.uk", address: "12 Workshop Lane, Birmingham B1 2CD", reg: "Co. Reg: 00000000  VAT: GB000000000" },
  customer: { name: "John Smith", address: "4 Elm Street, Birmingham B5 6EF", phone: "07700 900123", email: "john.smith@example.com" },
  vehicle: "AB12 CDE · Ford Transit (2018)",
  meta: { number: "INV-1042", date: "12/06/2026", due: "26/06/2026", status: "PART PAID" },
  labour: [
    { d: "Full service — oil, filters, inspection", q: 1, unit: "£120.00", vat: "£24.00", total: "£120.00" },
    { d: "Front brake pads & discs — labour 1.5h", q: 1, unit: "£67.50", vat: "£13.50", total: "£67.50" },
  ],
  parts: [
    { d: "Front brake pads (pair)", q: 1, unit: "£58.00", vat: "£11.60", total: "£58.00" },
    { d: "Front brake discs (pair)", q: 1, unit: "£96.00", vat: "£19.20", total: "£96.00" },
  ],
  totals: { subtotal: "£341.50", vat: "£68.30", total: "£409.80", paid: "£200.00", balance: "£209.80" },
};

function Row({ items, startNo, accent }) {
  return items.map((it, i) => (
    <tr key={i} style={{ borderBottom: "1px solid #E2E8F0", background: (startNo + i) % 2 === 0 ? "#F8FAFB" : "#fff" }}>
      <td style={{ padding: "5px 6px", textAlign: "center", color: "#334155" }}>{startNo + i}</td>
      <td style={{ padding: "5px 6px", color: "#0F172A" }}>{it.d}</td>
      <td style={{ padding: "5px 6px", textAlign: "center", color: "#334155" }}>{it.q}</td>
      <td style={{ padding: "5px 6px", textAlign: "right", color: "#334155" }}>{it.unit}</td>
      <td style={{ padding: "5px 6px", textAlign: "right", color: "#334155" }}>{it.vat}</td>
      <td style={{ padding: "5px 6px", textAlign: "right", color: "#0F172A", fontWeight: 600 }}>{it.total}</td>
    </tr>
  ));
}

function ItemsTable({ accent, dark, rows, startNo = 1 }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginTop: 4 }}>
      <thead>
        <tr style={{ background: "#F0F2F5", borderBottom: `2px solid ${accent}` }}>
          {["NO", "DESCRIPTION", "QTY", "UNIT", "VAT", "TOTAL"].map((h, i) => (
            <th key={h} style={{ padding: "6px", textAlign: i === 0 || i === 2 ? "center" : i >= 3 ? "right" : "left", color: dark, fontSize: 10 }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody><Row items={rows} startNo={startNo} accent={accent} /></tbody>
    </table>
  );
}

export function InvoiceDesignPreview({ design, garageName, logoUrl }) {
  const d = resolveDesign(design);
  const ff = FONT_FAMILY[d.font];
  const g = { ...DEMO.garage, name: garageName || DEMO.garage.name };
  const pad = d.density === "compact" ? 10 : 16;
  const logoPx = Math.round(d.logo_size * 2.5);
  const logoFit = d.logo_crop ? "cover" : "contain";

  const headerNode = (() => {
    if (d.header_style === "band") {
      return (
        <div style={{ background: d.accent, color: "#fff", padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderRadius: 2 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {d.show_logo && logoUrl && <img src={logoUrl} alt="" style={{ height: logoPx, width: logoPx, objectFit: logoFit, background: "#fff", borderRadius: 4, padding: 2 }} />}
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, textTransform: "uppercase", letterSpacing: 0.3 }}>{g.name}</div>
              {g.tagline && <div style={{ fontSize: 9, color: "#E2E8F0" }}>{g.tagline}</div>}
            </div>
          </div>
          <div style={{ fontWeight: 700, fontSize: 24 }}>INVOICE</div>
        </div>
      );
    }
    const titleSize = d.header_style === "minimal" ? 18 : 28;
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: d.header_style === "minimal" ? "baseline" : "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {d.show_logo && logoUrl && <img src={logoUrl} alt="" style={{ height: logoPx, width: logoPx, objectFit: logoFit }} />}
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, textTransform: "uppercase", color: "#0F172A" }}>{g.name}</div>
              {g.tagline && <div style={{ fontSize: 9, color: "#64748B" }}>{g.tagline}</div>}
              <div style={{ fontSize: 8, color: "#64748B" }}>{g.reg}</div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 700, fontSize: titleSize, color: d.dark }}>INVOICE</div>
            <div style={{ fontSize: 9, color: "#64748B" }}>{g.website.toUpperCase()}</div>
          </div>
        </div>
        <div style={{ height: d.header_style === "minimal" ? 1 : 2, background: d.header_style === "minimal" ? "#E2E8F0" : d.accent, marginTop: 6 }} />
      </div>
    );
  })();

  return (
    <div style={{ fontFamily: ff, background: "#fff", color: "#0F172A", padding: pad, border: "1px solid #E2E8F0", borderRadius: 6, width: "100%" }}>
      {headerNode}

      {/* Bill to + meta */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginTop: 14 }}>
        <div style={{ fontSize: 11, lineHeight: 1.45 }}>
          <div style={{ color: "#64748B" }}>Invoice to :</div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{DEMO.customer.name}</div>
          <div style={{ color: "#64748B" }}>{DEMO.customer.address}</div>
          <div style={{ color: "#64748B" }}>{DEMO.customer.phone}</div>
          <div style={{ color: "#64748B" }}>{DEMO.customer.email}</div>
          {d.show_vehicle && <div style={{ color: "#64748B" }}>{DEMO.vehicle}</div>}
        </div>
        <div style={{ fontSize: 11, textAlign: "right", lineHeight: 1.6, minWidth: 150 }}>
          <div style={{ fontWeight: 700, fontSize: 12 }}>Invoice no : {DEMO.meta.number}</div>
          <div>Date : {DEMO.meta.date}</div>
          <div>Due : {DEMO.meta.due}</div>
          <div>Status : {DEMO.meta.status}</div>
        </div>
      </div>

      {/* Line items */}
      <div style={{ marginTop: 14 }}>
        {d.labour_parts === "grouped" ? (
          <>
            <div style={{ fontWeight: 700, fontSize: 11, margin: "0 0 2px" }}>LABOUR</div>
            <ItemsTable accent={d.accent} dark={d.dark} rows={DEMO.labour} startNo={1} />
            <div style={{ fontWeight: 700, fontSize: 11, margin: "10px 0 2px" }}>PARTS &amp; MATERIALS</div>
            <ItemsTable accent={d.accent} dark={d.dark} rows={DEMO.parts} startNo={DEMO.labour.length + 1} />
          </>
        ) : (
          <ItemsTable accent={d.accent} dark={d.dark} rows={[...DEMO.labour, ...DEMO.parts]} startNo={1} />
        )}
      </div>

      {/* Totals (right-aligned, fixed position) */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
        <div style={{ fontSize: 11, minWidth: 210 }}>
          {[["Subtotal", DEMO.totals.subtotal], ["VAT 20%", DEMO.totals.vat]].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}><span style={{ color: "#64748B" }}>{k}</span><span style={{ fontWeight: 600 }}>{v}</span></div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderTop: "1px solid #E2E8F0", fontWeight: 700, color: d.dark }}><span>Total</span><span>{DEMO.totals.total}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}><span style={{ color: "#64748B" }}>Amount Paid</span><span style={{ fontWeight: 600 }}>{DEMO.totals.paid}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 8px", background: `${d.accent}14`, border: `1px solid ${d.accent}`, borderRadius: 3, fontWeight: 700, color: d.dark }}><span>Balance Due</span><span>{DEMO.totals.balance}</span></div>
        </div>
      </div>

      {/* Reorderable lower blocks */}
      {(() => {
        const blocks = {
          payment_details: d.show_payment_details ? (
            <div key="payment_details" style={{ fontSize: 10.5, color: "#64748B", lineHeight: 1.5, marginTop: 14 }}>
              <div style={{ fontWeight: 700, color: "#0F172A", marginBottom: 2 }}>PAYMENT DETAILS</div>
              <div>Bank Name : Demo Bank</div>
              <div>Account Name : Demo Motors Ltd</div>
              <div>Sort Code : 00-00-00</div>
              <div>Account Number : 00000000</div>
            </div>
          ) : null,
          thank_you: d.show_thank_you ? (
            <div key="thank_you" style={{ fontWeight: 700, fontSize: 12, marginTop: 14 }}>Thank you for business with us!</div>
          ) : null,
          terms: d.show_terms ? (
            <div key="terms" style={{ fontSize: 9, color: "#64748B", marginTop: 14 }}>
              <div style={{ fontWeight: 700, color: "#0F172A", fontSize: 10.5 }}>Terms &amp; Conditions</div>
              <div>Payment due within 14 days. Goods remain the property of the garage until paid in full.</div>
            </div>
          ) : null,
          signature: d.show_signature ? (
            <div key="signature" style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>A. Mechanic</div>
              <div style={{ fontWeight: 700, fontSize: 9, color: "#64748B" }}>Administrator</div>
            </div>
          ) : null,
        };
        return d.section_order.map((k) => blocks[k]).filter(Boolean);
      })()}

      <div style={{ height: 1, background: "#E2E8F0", margin: "10px 0 6px" }} />
      {d.footer_text && <div style={{ fontSize: 9, color: "#64748B" }}>{d.footer_text}</div>}
      {d.show_contact && <div style={{ fontSize: 9, color: "#64748B" }}>{g.phone}&nbsp;&nbsp;&nbsp;{g.email}&nbsp;&nbsp;&nbsp;{g.address}</div>}
    </div>
  );
}
